// utils/tokenEconomy.js
// UniLedge Token Economy System
// ============================================
// Token Value: 1 token = $0.10
// 10 tokens = $1.00 | 100 tokens = $10.00 | 1000 tokens = $100.00
// ============================================
// UPDATED: Jan 8, 2026 - TRUE HYBRID System Implementation
// Rule: Users get WHICHEVER IS LESS: tier % OR tier $ cap

// ============================================
// CORE TOKEN CONFIGURATION
// ============================================

export const TOKEN_CONFIG = {
  // ============================================
  // CORE TOKEN VALUE
  // ============================================
  VALUE_PER_TOKEN: 0.10,
  
  // ============================================
  // TRUE HYBRID LOYALTY REDEMPTION SYSTEM
  // Rule: Users get WHICHEVER IS LESS: tier % OR tier $ cap
  // Platinum is the ONLY tier with uncapped 40%
  // ============================================
  LOYALTY_REDEMPTION_CAPS: {
    NEW_USER: { percent: 0.10, maxUSD: 15, maxTokens: 150, label: 'New Member', expirationDays: 90 },
    BRONZE: { percent: 0.15, maxUSD: 25, maxTokens: 250, label: 'Bronze', expirationDays: 180 },
    SILVER: { percent: 0.20, maxUSD: 40, maxTokens: 400, label: 'Silver', expirationDays: 365 },
    GOLD: { percent: 0.30, maxUSD: 75, maxTokens: 750, label: 'Gold', expirationDays: 730 },
    PLATINUM: { percent: 0.40, maxUSD: Infinity, maxTokens: Infinity, label: 'Platinum', expirationDays: Infinity },
  },
  
  // ============================================
  // TIER REQUIREMENTS
  // How users progress through loyalty tiers
  // ============================================
  TIER_REQUIREMENTS: {
    NEW_USER: { purchases: 0, referrals: 0 },
    BRONZE: { purchases: 5, referrals: 0 },
    SILVER: { purchases: 15, referrals: 2 },
    GOLD: { purchases: 30, referrals: 5 },
    PLATINUM: { purchases: 50, referrals: 10 },
  },
  
  // ============================================
  // WELCOME & ONBOARDING BONUSES
  // ============================================
  WELCOME_BONUS: 100,                // $10 value - new user signup
  REFERRAL_BONUS_SHARER: 50,         // $5 value - person who referred
  REFERRAL_BONUS_NEW_USER: 50,       // $5 value - new user with referral code
  PROFILE_COMPLETION_BONUS: 20,      // $2 value - completing profile
  FIRST_PURCHASE_BONUS: 25,          // $2.50 value - first purchase
  TOUR_COMPLETION_BONUS: 10,         // $1 value - completing AVA tour
  
  // ============================================
  // PURCHASE REWARDS
  // ============================================
  BASE_PURCHASE_TOKENS: 10,          // Base tokens per item
  BULK_MULTIPLIER_3_PLUS: 1.25,      // 25% extra for 3+ items
  BULK_MULTIPLIER_5_PLUS: 1.50,      // 50% extra for 5+ items
  PICKUP_BONUS: 5,                   // $0.50 for choosing pickup
  
  // ============================================
  // JOB/WORK EARN RATES (Conservative - Launch Phase)
  // Target: 50-80 tokens/hour = $5-8/hour equivalent
  // Workers are LOCAL (<2 miles), minimal gas/vehicle costs
  // Value proposition: Earn grocery credits, not salary replacement
  // ============================================
  BASE_HOURLY_TOKEN_RATE: 60,        // $6/hr base rate
  MIN_HOURLY_TOKEN_RATE: 50,         // $5/hr minimum
  MAX_HOURLY_TOKEN_RATE: 80,         // $8/hr maximum
  
  // Job type base tokens (before regional adjustment)
  JOB_RATES: {
    LOCAL_DELIVERY_SHORT: 25,        // <1 mile, ~20 min, $2.50
    LOCAL_DELIVERY_MEDIUM: 40,       // 1-2 miles, ~30 min, $4.00
    LOCAL_DELIVERY_LONG: 60,         // 2-3 miles, ~45 min, $6.00
    ROUTE_COORDINATION: 80,          // 1 hour, $8.00
    GROUP_ADMIN_TASK: 35,            // 30 min, $3.50
    INVENTORY_ASSIST: 70,            // 1 hour, $7.00
    QUALITY_CHECK: 25,               // 15 min, $2.50
  },
  
  // Performance bonuses
  JOB_BONUSES: {
    ON_TIME_COMPLETION: 10,          // $1 bonus
    FIVE_STAR_RATING: 20,            // $2 bonus
    FIRST_JOB_OF_DAY: 15,            // $1.50 bonus
    STREAK_BONUS_3: 25,              // 3 jobs in a row, $2.50
    STREAK_BONUS_5: 50,              // 5 jobs in a row, $5.00
  },
  
  // ============================================
  // REGIONAL PRICE PARITY MULTIPLIERS
  // Based on Bureau of Economic Analysis RPP data
  // National average = 1.0 (100)
  // ============================================
  REGIONAL_MULTIPLIERS: {
    // Low cost regions (RPP 85-95)
    LOW_COST: 0.90,       // MS, AR, AL, WV, etc.
    
    // Average cost regions (RPP 95-105)  
    AVERAGE_COST: 1.00,   // Most of US, including Sacramento
    
    // Above average cost (RPP 105-115)
    ABOVE_AVERAGE: 1.10,  // LA, Seattle, Denver, etc.
    
    // High cost regions (RPP 115-130)
    HIGH_COST: 1.20,      // SF, NYC, Boston, etc.
    
    // Very high cost (RPP 130+)
    VERY_HIGH_COST: 1.30, // Manhattan, San Jose, etc.
  },
  
  // ============================================
  // TOKEN EXPIRATION
  // Creates urgency, keeps flywheel spinning
  // ============================================
  TOKEN_EXPIRATION_DAYS: 90,
  
  // ============================================
  // LEGACY ALIASES (backward compatibility)
  // ============================================
  SHARER_REWARD: 50,
  REFERRAL_LINK_BONUS: 50,
};

// ============================================
// TOKEN VALUE HELPERS
// ============================================

/**
 * Convert tokens to USD value
 * @param {number} tokens - Number of tokens
 * @returns {number} USD value
 */
export const tokensToUSD = (tokens) => {
  if (typeof tokens !== 'number' || isNaN(tokens)) return 0;
  return tokens * TOKEN_CONFIG.VALUE_PER_TOKEN;
};

/**
 * Convert USD to token amount
 * @param {number} usd - USD amount
 * @returns {number} Number of tokens
 */
export const usdToTokens = (usd) => {
  if (typeof usd !== 'number' || isNaN(usd)) return 0;
  return Math.floor(usd / TOKEN_CONFIG.VALUE_PER_TOKEN);
};

/**
 * Format token amount for display
 * @param {number} tokens - Number of tokens
 * @returns {string} Formatted string
 */
export const formatTokens = (tokens) => {
  if (typeof tokens !== 'number' || isNaN(tokens)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.floor(tokens));
};

/**
 * Format token value as USD for display
 * @param {number} tokens - Number of tokens
 * @returns {string} Formatted USD string
 */
export const formatTokenValue = (tokens) => {
  if (typeof tokens !== 'number' || isNaN(tokens)) return '$0.00';
  const value = tokensToUSD(tokens);
  return `$${value.toFixed(2)}`;
};

// ============================================
// ONBOARDING BONUS HELPERS
// ============================================

/**
 * Get welcome bonus details for new users
 * @returns {Object} Welcome bonus info
 */
export const getWelcomeBonus = () => {
  return {
    tokens: TOKEN_CONFIG.WELCOME_BONUS,
    value: tokensToUSD(TOKEN_CONFIG.WELCOME_BONUS),
    valueFormatted: formatTokenValue(TOKEN_CONFIG.WELCOME_BONUS),
    message: `Welcome! You received ${TOKEN_CONFIG.WELCOME_BONUS} tokens (${formatTokenValue(TOKEN_CONFIG.WELCOME_BONUS)}) to start your UniLedge journey!`
  };
};

/**
 * Get referral bonus details
 * @param {boolean} isSharer - Is this for the person who shared?
 * @returns {Object} Referral bonus info
 */
export const getReferralBonus = (isSharer = false) => {
  const tokens = isSharer 
    ? TOKEN_CONFIG.REFERRAL_BONUS_SHARER 
    : TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER;
  
  return {
    tokens,
    value: tokensToUSD(tokens),
    valueFormatted: formatTokenValue(tokens),
    message: isSharer
      ? `You earned ${tokens} tokens (${formatTokenValue(tokens)}) for referring a friend!`
      : `You received ${tokens} bonus tokens (${formatTokenValue(tokens)}) from your friend's referral!`
  };
};

/**
 * Get profile completion bonus details
 * @returns {Object} Profile bonus info
 */
export const getProfileCompletionBonus = () => {
  return {
    tokens: TOKEN_CONFIG.PROFILE_COMPLETION_BONUS,
    value: tokensToUSD(TOKEN_CONFIG.PROFILE_COMPLETION_BONUS),
    valueFormatted: formatTokenValue(TOKEN_CONFIG.PROFILE_COMPLETION_BONUS),
    message: `Complete your profile to earn ${TOKEN_CONFIG.PROFILE_COMPLETION_BONUS} tokens (${formatTokenValue(TOKEN_CONFIG.PROFILE_COMPLETION_BONUS)})!`
  };
};

/**
 * Get tour completion bonus details
 * @returns {Object} Tour bonus info
 */
export const getTourCompletionBonus = () => {
  return {
    tokens: TOKEN_CONFIG.TOUR_COMPLETION_BONUS,
    value: tokensToUSD(TOKEN_CONFIG.TOUR_COMPLETION_BONUS),
    valueFormatted: formatTokenValue(TOKEN_CONFIG.TOUR_COMPLETION_BONUS),
    message: `You earned ${TOKEN_CONFIG.TOUR_COMPLETION_BONUS} tokens for completing the tour!`
  };
};

/**
 * Get all onboarding bonuses for display
 * @returns {Object} All onboarding bonuses
 */
export const getOnboardingBonusesDisplay = () => {
  const totalPossible = TOKEN_CONFIG.WELCOME_BONUS + 
                        TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER + 
                        TOKEN_CONFIG.PROFILE_COMPLETION_BONUS + 
                        TOKEN_CONFIG.TOUR_COMPLETION_BONUS;
  
  return {
    welcome: {
      tokens: TOKEN_CONFIG.WELCOME_BONUS,
      value: formatTokenValue(TOKEN_CONFIG.WELCOME_BONUS),
      description: 'Just for signing up'
    },
    referral: {
      sharerTokens: TOKEN_CONFIG.REFERRAL_BONUS_SHARER,
      sharerValue: formatTokenValue(TOKEN_CONFIG.REFERRAL_BONUS_SHARER),
      newUserTokens: TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER,
      newUserValue: formatTokenValue(TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER),
      totalValue: formatTokenValue(TOKEN_CONFIG.REFERRAL_BONUS_SHARER + TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER),
      description: 'You and your friend each get $5'
    },
    profileCompletion: {
      tokens: TOKEN_CONFIG.PROFILE_COMPLETION_BONUS,
      value: formatTokenValue(TOKEN_CONFIG.PROFILE_COMPLETION_BONUS),
      description: 'Complete your profile'
    },
    tour: {
      tokens: TOKEN_CONFIG.TOUR_COMPLETION_BONUS,
      value: formatTokenValue(TOKEN_CONFIG.TOUR_COMPLETION_BONUS),
      description: 'Complete the AVA tour'
    },
    totalPossible: {
      tokens: totalPossible,
      value: formatTokenValue(totalPossible),
      description: 'Total possible from onboarding (with referral)'
    }
  };
};

// ============================================
// PURCHASE TOKEN CALCULATIONS
// ============================================

/**
 * Calculate bulk multiplier based on quantity
 * @param {number} quantity - Number of items
 * @returns {number} Multiplier (1, 1.25, or 1.5)
 */
export const getBulkMultiplier = (quantity) => {
  if (quantity >= 5) return TOKEN_CONFIG.BULK_MULTIPLIER_5_PLUS;
  if (quantity >= 3) return TOKEN_CONFIG.BULK_MULTIPLIER_3_PLUS;
  return 1;
};

/**
 * Calculate tokens earned from a purchase
 * @param {Object} options - Purchase details
 * @returns {Object} Token breakdown
 */
export const calculatePurchaseTokens = ({ 
  quantity = 1, 
  usedReferralLink = false, 
  isFirstPurchase = false,
  isPickup = false 
}) => {
  const baseTokens = TOKEN_CONFIG.BASE_PURCHASE_TOKENS * quantity;
  const bulkMultiplier = getBulkMultiplier(quantity);
  const bulkTokens = Math.floor(baseTokens * bulkMultiplier);
  const bulkBonus = bulkTokens - baseTokens;
  
  const referralBonus = usedReferralLink ? TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER : 0;
  const firstPurchaseBonus = isFirstPurchase ? TOKEN_CONFIG.FIRST_PURCHASE_BONUS : 0;
  const pickupBonus = isPickup ? TOKEN_CONFIG.PICKUP_BONUS : 0;
  
  const totalTokens = bulkTokens + referralBonus + firstPurchaseBonus + pickupBonus;
  
  return {
    baseTokens,
    bulkMultiplier,
    bulkBonus,
    referralBonus,
    firstPurchaseBonus,
    pickupBonus,
    totalTokens,
    totalValue: tokensToUSD(totalTokens)
  };
};

/**
 * Calculate tokens for a cart of items
 * @param {Array} cartItems - Array of cart items with quantity
 * @param {Object} options - Additional options
 * @returns {Object} Token breakdown for entire cart
 */
export const calculateCartTokens = (cartItems, options = {}) => {
  const totalQuantity = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  
  return calculatePurchaseTokens({
    quantity: totalQuantity,
    ...options
  });
};

// ============================================
// JOB TOKEN CALCULATIONS
// ============================================

/**
 * Get regional multiplier based on location
 * @param {string} region - Region code or RPP value
 * @returns {number} Regional multiplier
 */
export const getRegionalMultiplier = (region) => {
  // If passed a specific multiplier key
  if (TOKEN_CONFIG.REGIONAL_MULTIPLIERS[region]) {
    return TOKEN_CONFIG.REGIONAL_MULTIPLIERS[region];
  }
  
  // If passed an RPP value (number)
  if (typeof region === 'number') {
    if (region < 90) return TOKEN_CONFIG.REGIONAL_MULTIPLIERS.LOW_COST;
    if (region < 105) return TOKEN_CONFIG.REGIONAL_MULTIPLIERS.AVERAGE_COST;
    if (region < 115) return TOKEN_CONFIG.REGIONAL_MULTIPLIERS.ABOVE_AVERAGE;
    if (region < 130) return TOKEN_CONFIG.REGIONAL_MULTIPLIERS.HIGH_COST;
    return TOKEN_CONFIG.REGIONAL_MULTIPLIERS.VERY_HIGH_COST;
  }
  
  // Default to average
  return TOKEN_CONFIG.REGIONAL_MULTIPLIERS.AVERAGE_COST;
};

/**
 * Calculate job token reward
 * @param {string} jobType - Type of job from JOB_RATES
 * @param {Object} options - Additional options
 * @returns {Object} Token reward details
 */
export const calculateJobTokens = (jobType, options = {}) => {
  const {
    region = 'AVERAGE_COST',
    onTime = false,
    rating = null,
    isFirstJobOfDay = false,
    streakCount = 0
  } = options;
  
  // Base tokens for job type
  const baseTokens = TOKEN_CONFIG.JOB_RATES[jobType] || TOKEN_CONFIG.BASE_HOURLY_TOKEN_RATE;
  
  // Regional adjustment
  const regionalMultiplier = getRegionalMultiplier(region);
  const adjustedBase = Math.round(baseTokens * regionalMultiplier);
  
  // Calculate bonuses
  let bonusTokens = 0;
  const bonusBreakdown = {};
  
  if (onTime) {
    bonusTokens += TOKEN_CONFIG.JOB_BONUSES.ON_TIME_COMPLETION;
    bonusBreakdown.onTime = TOKEN_CONFIG.JOB_BONUSES.ON_TIME_COMPLETION;
  }
  
  if (rating === 5) {
    bonusTokens += TOKEN_CONFIG.JOB_BONUSES.FIVE_STAR_RATING;
    bonusBreakdown.fiveStarRating = TOKEN_CONFIG.JOB_BONUSES.FIVE_STAR_RATING;
  }
  
  if (isFirstJobOfDay) {
    bonusTokens += TOKEN_CONFIG.JOB_BONUSES.FIRST_JOB_OF_DAY;
    bonusBreakdown.firstJobOfDay = TOKEN_CONFIG.JOB_BONUSES.FIRST_JOB_OF_DAY;
  }
  
  if (streakCount >= 5) {
    bonusTokens += TOKEN_CONFIG.JOB_BONUSES.STREAK_BONUS_5;
    bonusBreakdown.streak = TOKEN_CONFIG.JOB_BONUSES.STREAK_BONUS_5;
  } else if (streakCount >= 3) {
    bonusTokens += TOKEN_CONFIG.JOB_BONUSES.STREAK_BONUS_3;
    bonusBreakdown.streak = TOKEN_CONFIG.JOB_BONUSES.STREAK_BONUS_3;
  }
  
  const totalTokens = adjustedBase + bonusTokens;
  
  return {
    jobType,
    baseTokens,
    regionalMultiplier,
    adjustedBase,
    bonusTokens,
    bonusBreakdown,
    totalTokens,
    totalValue: tokensToUSD(totalTokens),
    totalValueFormatted: formatTokenValue(totalTokens),
    hourlyEquivalent: `$${(tokensToUSD(totalTokens)).toFixed(2)}/job`
  };
};

/**
 * Get job rates display for UI
 * @param {string} region - Region code
 * @returns {Object} Job rates for display
 */
export const getJobRatesDisplay = (region = 'AVERAGE_COST') => {
  const multiplier = getRegionalMultiplier(region);
  
  return Object.entries(TOKEN_CONFIG.JOB_RATES).map(([type, baseTokens]) => {
    const adjustedTokens = Math.round(baseTokens * multiplier);
    return {
      type,
      typeName: type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
      baseTokens,
      adjustedTokens,
      value: formatTokenValue(adjustedTokens),
      regionalMultiplier: multiplier
    };
  });
};

// ============================================
// TOKEN REDEMPTION SYSTEM - TRUE HYBRID
// Rule: Users get WHICHEVER IS LESS: tier % OR tier $ cap
// ============================================

/**
 * Calculate maximum tokens that can be redeemed for a cart
 * TRUE HYBRID APPROACH: tier % of cart OR tier $ cap, whichever is LESS
 * PLATINUM: Uncapped - full 40% always
 * @param {number} cartTotal - Cart total in USD
 * @param {number} userTokenBalance - User's available token balance
 * @param {string} loyaltyTier - User's loyalty tier
 * @returns {Object} Redemption details
 */
export const calculateMaxRedemption = (cartTotal, userTokenBalance, loyaltyTier = 'NEW_USER') => {
  // Handle invalid inputs
  if (typeof cartTotal !== 'number' || isNaN(cartTotal)) cartTotal = 0;
  if (typeof userTokenBalance !== 'number' || isNaN(userTokenBalance)) userTokenBalance = 0;
  
  // Get loyalty tier config with percent
  const tierConfig = TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS[loyaltyTier] || 
                     TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS.NEW_USER;
  
  // TRUE HYBRID: Calculate tier's percentage of cart
  const tierPercent = tierConfig.percent || 0.10;
  const percentBasedValue = cartTotal * tierPercent;
  const percentBasedTokens = usdToTokens(percentBasedValue);
  
  // Check if Platinum (uncapped)
  const isUncapped = tierConfig.maxUSD === Infinity;
  
  // TRUE HYBRID: Apply the LOWER of: tier % of cart OR tier $ cap
  const cappedValue = isUncapped ? percentBasedValue : Math.min(percentBasedValue, tierConfig.maxUSD);
  const cappedTokens = isUncapped ? percentBasedTokens : Math.min(percentBasedTokens, tierConfig.maxTokens);
  
  // Can't redeem more than user has
  const actualMaxTokens = Math.min(cappedTokens, userTokenBalance);
  const actualMaxValue = tokensToUSD(actualMaxTokens);
  
  // Determine what's limiting the redemption
  let limitReason = null;
  if (userTokenBalance < cappedTokens) {
    limitReason = 'balance';
  } else if (!isUncapped && tierConfig.maxUSD < percentBasedValue) {
    limitReason = 'cap';
  } else {
    limitReason = 'percent';
  }
  
  return {
    // Tier info
    tierPercent,
    tierPercentDisplay: `${Math.round(tierPercent * 100)}%`,
    
    // Cart info
    cartTotal,
    
    // Loyalty info
    loyaltyTier,
    loyaltyLabel: tierConfig.label,
    loyaltyMaxUSD: isUncapped ? 'Unlimited' : tierConfig.maxUSD,
    loyaltyMaxTokens: isUncapped ? 'Unlimited' : tierConfig.maxTokens,
    isUncapped,
    
    // Percentage-based calculation
    percentBasedValue,
    percentBasedTokens,
    
    // Capped values (before balance check)
    maxRedemptionTokens: cappedTokens,
    maxRedemptionValue: cappedValue,
    
    // User balance
    userBalance: userTokenBalance,
    userBalanceValue: tokensToUSD(userTokenBalance),
    
    // Actual max (after all limits)
    actualMaxTokens,
    actualMaxValue,
    
    // Status
    canRedeem: actualMaxTokens > 0,
    limitedByBalance: userTokenBalance < cappedTokens,
    limitedByCap: !isUncapped && tierConfig.maxUSD < percentBasedValue,
    limitReason
  };
};

/**
 * Apply token redemption to cart
 * @param {number} cartTotal - Cart total in USD
 * @param {number} tokensToRedeem - Number of tokens user wants to redeem
 * @param {number} userTokenBalance - User's available balance
 * @param {string} loyaltyTier - User's loyalty tier
 * @returns {Object} Redemption result
 */
export const applyTokenRedemption = (cartTotal, tokensToRedeem, userTokenBalance, loyaltyTier = 'NEW_USER') => {
  const maxRedemption = calculateMaxRedemption(cartTotal, userTokenBalance, loyaltyTier);
  
  // Enforce limits
  const actualTokensToRedeem = Math.min(
    tokensToRedeem || 0, 
    maxRedemption.actualMaxTokens
  );
  const discountAmount = tokensToUSD(actualTokensToRedeem);
  const newCartTotal = Math.max(0, cartTotal - discountAmount);
  const remainingBalance = userTokenBalance - actualTokensToRedeem;
  
  // Calculate effective discount percentage
  const effectiveDiscount = cartTotal > 0 ? (discountAmount / cartTotal) * 100 : 0;
  
  return {
    originalTotal: cartTotal,
    tokensRedeemed: actualTokensToRedeem,
    discountAmount,
    discountFormatted: `$${discountAmount.toFixed(2)}`,
    newTotal: newCartTotal,
    remainingBalance,
    effectiveDiscount: `${effectiveDiscount.toFixed(1)}%`,
    wasLimited: (tokensToRedeem || 0) > actualTokensToRedeem,
    limitReason: (tokensToRedeem || 0) > actualTokensToRedeem 
      ? `Maximum redemption is ${formatTokens(maxRedemption.actualMaxTokens)} tokens ($${maxRedemption.actualMaxValue.toFixed(2)})`
      : null,
    loyaltyTier: maxRedemption.loyaltyLabel,
    tierPercent: maxRedemption.tierPercent
  };
};

/**
 * Get next loyalty tier info for upsell messaging
 * @param {string} currentTier - Current loyalty tier
 * @returns {Object|null} Next tier info or null if at max
 */
export const getNextLoyaltyTier = (currentTier = 'NEW_USER') => {
  const tiers = ['NEW_USER', 'BRONZE', 'SILVER', 'GOLD', 'PLATINUM'];
  const currentIndex = tiers.indexOf(currentTier);
  
  if (currentIndex < tiers.length - 1) {
    const nextTierKey = tiers[currentIndex + 1];
    const nextTier = TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS[nextTierKey];
    const currentTierData = TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS[currentTier];
    const nextReqs = TOKEN_CONFIG.TIER_REQUIREMENTS[nextTierKey];
    
    // Calculate the actual benefit increase
    const currentPercent = currentTierData?.percent || 0.10;
    const nextPercent = nextTier.percent;
    const percentIncrease = Math.round((nextPercent - currentPercent) * 100);
    
    return {
      currentTier: currentTier,
      currentLabel: currentTierData?.label || 'New Member',
      currentPercent: currentPercent,
      currentMaxUSD: currentTierData?.maxUSD || 15,
      nextTier: nextTierKey,
      nextLabel: nextTier.label,
      nextPercent: nextPercent,
      nextMaxUSD: nextTier.maxUSD === Infinity ? 'Unlimited' : nextTier.maxUSD,
      nextRequirements: nextReqs,
      percentIncrease,
      message: nextTier.maxUSD === Infinity
        ? `Reach ${nextTier.label} for UNCAPPED ${Math.round(nextPercent * 100)}% redemption!`
        : `Reach ${nextTier.label} to unlock ${Math.round(nextPercent * 100)}% redemption (up to $${nextTier.maxUSD})!`
    };
  }
  
  return null; // Already at max tier (Platinum)
};

/**
 * Calculate user's loyalty tier based on activity
 * @param {number} totalPurchases - Total completed purchases
 * @param {number} successfulReferrals - Total successful referrals
 * @returns {Object} Tier info
 */
export const calculateLoyaltyTier = (totalPurchases = 0, successfulReferrals = 0) => {
  const tiers = ['PLATINUM', 'GOLD', 'SILVER', 'BRONZE', 'NEW_USER'];
  
  for (const tier of tiers) {
    const reqs = TOKEN_CONFIG.TIER_REQUIREMENTS[tier];
    if (totalPurchases >= reqs.purchases && successfulReferrals >= reqs.referrals) {
      const tierConfig = TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS[tier];
      return {
        tier,
        label: tierConfig.label,
        percent: tierConfig.percent,
        maxRedemptionUSD: tierConfig.maxUSD,
        maxRedemptionTokens: tierConfig.maxTokens,
        expirationDays: tierConfig.expirationDays,
        requirements: reqs
      };
    }
  }
  
  const newUserConfig = TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS.NEW_USER;
  return {
    tier: 'NEW_USER',
    label: 'New Member',
    percent: newUserConfig.percent,
    maxRedemptionUSD: 15,
    maxRedemptionTokens: 150,
    expirationDays: 90,
    requirements: TOKEN_CONFIG.TIER_REQUIREMENTS.NEW_USER
  };
};

/**
 * Get progress toward next tier
 * @param {number} totalPurchases - Total completed purchases
 * @param {number} successfulReferrals - Total successful referrals
 * @returns {Object} Progress info
 */
export const getTierProgress = (totalPurchases = 0, successfulReferrals = 0) => {
  const currentTierInfo = calculateLoyaltyTier(totalPurchases, successfulReferrals);
  const nextTierInfo = getNextLoyaltyTier(currentTierInfo.tier);
  
  if (!nextTierInfo) {
    return {
      currentTier: currentTierInfo,
      nextTier: null,
      isMaxTier: true,
      message: "🎉 You've reached Platinum! Enjoy UNCAPPED 40% token redemption!"
    };
  }
  
  const nextReqs = nextTierInfo.nextRequirements;
  const purchasesNeeded = Math.max(0, nextReqs.purchases - totalPurchases);
  const referralsNeeded = Math.max(0, nextReqs.referrals - successfulReferrals);
  
  const purchaseProgress = nextReqs.purchases > 0 
    ? Math.min(100, (totalPurchases / nextReqs.purchases) * 100) 
    : 100;
  const referralProgress = nextReqs.referrals > 0 
    ? Math.min(100, (successfulReferrals / nextReqs.referrals) * 100) 
    : 100;
  
  let message = '';
  if (purchasesNeeded > 0 && referralsNeeded > 0) {
    message = `${purchasesNeeded} more purchase${purchasesNeeded !== 1 ? 's' : ''} and ${referralsNeeded} more referral${referralsNeeded !== 1 ? 's' : ''} to ${nextTierInfo.nextLabel} (${Math.round(nextTierInfo.nextPercent * 100)}%)!`;
  } else if (purchasesNeeded > 0) {
    message = `${purchasesNeeded} more purchase${purchasesNeeded !== 1 ? 's' : ''} to ${nextTierInfo.nextLabel} (${Math.round(nextTierInfo.nextPercent * 100)}%)!`;
  } else if (referralsNeeded > 0) {
    message = `${referralsNeeded} more referral${referralsNeeded !== 1 ? 's' : ''} to ${nextTierInfo.nextLabel} (${Math.round(nextTierInfo.nextPercent * 100)}%)!`;
  }
  
  return {
    currentTier: currentTierInfo,
    nextTier: nextTierInfo,
    isMaxTier: false,
    purchasesNeeded,
    referralsNeeded,
    purchaseProgress,
    referralProgress,
    overallProgress: (purchaseProgress + referralProgress) / 2,
    message
  };
};

/**
 * Calculate token expiration based on tier and last activity
 * @param {string} loyaltyTier - User's loyalty tier
 * @param {Date} lastActivityDate - Date of last qualifying activity
 * @returns {Object} Expiration info
 */
export const calculateTierBasedExpiration = (loyaltyTier = 'NEW_USER', lastActivityDate = new Date()) => {
  const tierConfig = TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS[loyaltyTier] || 
                     TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS.NEW_USER;
  
  // Platinum never expires
  if (tierConfig.expirationDays === Infinity) {
    return {
      tier: loyaltyTier,
      neverExpires: true,
      expirationDate: null,
      daysRemaining: Infinity,
      message: "Your tokens never expire at Platinum level! 🎉"
    };
  }
  
  const expirationDate = new Date(lastActivityDate);
  expirationDate.setDate(expirationDate.getDate() + tierConfig.expirationDays);
  
  const now = new Date();
  const daysRemaining = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
  
  let message = '';
  let urgency = 'none';
  
  if (daysRemaining <= 0) {
    message = "Your tokens have expired due to inactivity. Make a purchase to earn new tokens!";
    urgency = 'expired';
  } else if (daysRemaining <= 7) {
    message = `⚠️ Your tokens expire in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}! Any activity will reset the clock.`;
    urgency = 'critical';
  } else if (daysRemaining <= 14) {
    message = `Your tokens expire in ${daysRemaining} days. Stay active to keep them!`;
    urgency = 'warning';
  } else if (daysRemaining <= 30) {
    message = `Your tokens expire in ${daysRemaining} days.`;
    urgency = 'notice';
  } else {
    message = `Your tokens are safe for ${daysRemaining} more days.`;
    urgency = 'none';
  }
  
  return {
    tier: loyaltyTier,
    neverExpires: false,
    expirationDate,
    daysRemaining,
    expirationDays: tierConfig.expirationDays,
    urgency,
    message
  };
};

/**
 * Get redemption display info for checkout UI
 * @param {number} cartTotal - Cart total
 * @param {number} userBalance - User's token balance
 * @param {string} loyaltyTier - User's loyalty tier
 * @returns {Object} Display info for redemption UI
 */
export const getRedemptionDisplay = (cartTotal, userBalance, loyaltyTier = 'NEW_USER') => {
  const maxRedemption = calculateMaxRedemption(cartTotal, userBalance, loyaltyTier);
  const nextLoyalty = getNextLoyaltyTier(loyaltyTier);
  const tierConfig = TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS[loyaltyTier] || 
                     TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS.NEW_USER;
  
  // Format max USD display
  const maxUSDDisplay = maxRedemption.isUncapped 
    ? 'Unlimited' 
    : `$${tierConfig.maxUSD}`;
  
  return {
    canRedeem: maxRedemption.canRedeem,
    maxPercent: maxRedemption.tierPercentDisplay,
    maxTokens: maxRedemption.actualMaxTokens,
    maxValue: `$${maxRedemption.actualMaxValue.toFixed(2)}`,
    maxRedeemableTokens: maxRedemption.actualMaxTokens,
    userBalance,
    userBalanceValue: formatTokenValue(userBalance),
    
    // Loyalty info
    loyaltyTier: maxRedemption.loyaltyLabel,
    loyaltyMaxUSD: tierConfig.maxUSD,
    tierPercent: tierConfig.percent,
    isUncapped: maxRedemption.isUncapped,
    
    // Explanation - TRUE HYBRID
    explanation: maxRedemption.isUncapped
      ? `Use up to ${maxRedemption.tierPercentDisplay} of your cart in tokens (UNCAPPED at ${maxRedemption.loyaltyLabel} level!)`
      : `Use up to ${maxRedemption.tierPercentDisplay} of your cart OR ${maxUSDDisplay} (whichever is less)`,
    
    // Upsell
    nextTierMessage: nextLoyalty?.message || '🎉 You\'ve unlocked maximum redemption (Platinum)!',
    limitedByBalance: maxRedemption.limitedByBalance,
    limitedByCap: maxRedemption.limitedByCap
  };
};

// ============================================
// TOKEN EXPIRATION HELPERS
// ============================================

/**
 * Calculate token expiration date
 * @param {Date} earnedDate - Date tokens were earned
 * @returns {Date} Expiration date
 */
export const calculateExpirationDate = (earnedDate = new Date()) => {
  const expiration = new Date(earnedDate);
  expiration.setDate(expiration.getDate() + TOKEN_CONFIG.TOKEN_EXPIRATION_DAYS);
  return expiration;
};

/**
 * Check if tokens are expired
 * @param {Date} earnedDate - Date tokens were earned
 * @returns {boolean} Whether tokens are expired
 */
export const isTokenExpired = (earnedDate) => {
  const expirationDate = calculateExpirationDate(earnedDate);
  return new Date() > expirationDate;
};

/**
 * Get days until token expiration
 * @param {Date} earnedDate - Date tokens were earned
 * @returns {number} Days until expiration (negative if expired)
 */
export const daysUntilExpiration = (earnedDate) => {
  const expirationDate = calculateExpirationDate(earnedDate);
  const now = new Date();
  const diffTime = expirationDate - now;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// ============================================
// DISPLAY HELPERS
// ============================================

/**
 * Get bulk bonus display info
 * @param {number} quantity - Current quantity
 * @returns {Object} Bulk bonus info for display
 */
export const getBulkBonusDisplay = (quantity) => {
  const multiplier = getBulkMultiplier(quantity);
  const isBonus = multiplier > 1;
  const bonusPercent = Math.round((multiplier - 1) * 100);
  
  let nextBonusAt = null;
  let nextBonusPercent = null;
  
  if (quantity < 3) {
    nextBonusAt = 3;
    nextBonusPercent = 25;
  } else if (quantity < 5) {
    nextBonusAt = 5;
    nextBonusPercent = 50;
  }
  
  return {
    isBonus,
    multiplier,
    bonusPercent,
    nextBonusAt,
    nextBonusPercent,
    currentMessage: isBonus ? `BULK HERO! +${bonusPercent}% extra tokens` : null,
    nextMessage: nextBonusAt 
      ? `Add ${nextBonusAt - quantity} more for ${nextBonusPercent}% bonus tokens!`
      : 'Maximum bulk bonus achieved!'
  };
};

// ============================================
// CAC (Customer Acquisition Cost) TRACKING
// ============================================

/**
 * Calculate effective CAC for a user acquisition
 * @param {Object} acquisitionDetails - How user was acquired
 * @returns {Object} CAC breakdown
 */
export const calculateEffectiveCAC = (acquisitionDetails = {}) => {
  const {
    wasReferred = false,
    completedProfile = false,
    completedTour = false
  } = acquisitionDetails;
  
  let totalTokensIssued = TOKEN_CONFIG.WELCOME_BONUS;
  
  if (wasReferred) {
    totalTokensIssued += TOKEN_CONFIG.REFERRAL_BONUS_SHARER; // Paid to referrer
  }
  
  if (completedProfile) {
    totalTokensIssued += TOKEN_CONFIG.PROFILE_COMPLETION_BONUS;
  }
  
  if (completedTour) {
    totalTokensIssued += TOKEN_CONFIG.TOUR_COMPLETION_BONUS;
  }
  
  const faceValueCAC = tokensToUSD(totalTokensIssued);
  
  // Actual cost is ~70% of face value (after margin when redeemed)
  // And ~20% of tokens are never redeemed
  const estimatedRedemptionRate = 0.80;
  const marginAfterRedemption = 0.70;
  const actualCAC = faceValueCAC * estimatedRedemptionRate * marginAfterRedemption;
  
  return {
    totalTokensIssued,
    faceValueCAC,
    faceValueCACFormatted: `$${faceValueCAC.toFixed(2)}`,
    estimatedActualCAC: actualCAC,
    estimatedActualCACFormatted: `$${actualCAC.toFixed(2)}`,
    breakdown: {
      welcomeBonus: TOKEN_CONFIG.WELCOME_BONUS,
      referralPaid: wasReferred ? TOKEN_CONFIG.REFERRAL_BONUS_SHARER : 0,
      profileBonus: completedProfile ? TOKEN_CONFIG.PROFILE_COMPLETION_BONUS : 0,
      tourBonus: completedTour ? TOKEN_CONFIG.TOUR_COMPLETION_BONUS : 0
    },
    comparison: {
      facebookAdsCAC: '$50-150',
      googleAdsCAC: '$75-200',
      uniledgeCAC: `$${actualCAC.toFixed(2)}`,
      savings: 'Up to 90% lower CAC'
    }
  };
};

// ============================================
// LEGACY SUPPORT (for existing job-based token system)
// ============================================

export const TOKEN_POLICIES = {
  MAX_TOKENS_BY_TIER: {
    1: { base: 20, perHour: 15 },
    2: { base: 30, perHour: 25 },
    3: { base: 50, perHour: 40 }
  },
  PLATFORM_FEE_PERCENTAGE: 7.5,
  TOKEN_ALLOCATION_RATE: 0.5,
  GROUP_MONTHLY_TOKEN_BUDGET: 1000,
  TRUST_BUDGET_MULTIPLIERS: {
    '3.0': 0.5,
    '3.5': 0.75,
    '4.0': 1.0,
    '4.5': 1.5
  },
  MAX_TOKENS_PER_JOB: 500,
  MIN_JOB_POSTING_INTERVAL: 30,
  MAX_JOBS_PER_DAY: 5
};

// Keep legacy functions for job system
export const getTokenValue = (tokens) => tokens;
export const formatTokenAmount = formatTokens;

// Alias for backward compatibility
export const getDaysUntilExpiration = daysUntilExpiration;

/**
 * Calculate earnings for a work session
 * @param {number} hours - Hours worked
 * @param {string} region - Region code for multiplier
 * @returns {Object} Session earnings estimate
 */
export const calculateSessionEarnings = (hours = 1, region = 'AVERAGE_COST') => {
  const regionalMultiplier = getRegionalMultiplier(region);
  const baseTokensPerHour = TOKEN_CONFIG.BASE_HOURLY_TOKEN_RATE || 60;
  const adjustedRate = Math.round(baseTokensPerHour * regionalMultiplier);
  const estimatedTokens = Math.floor(adjustedRate * hours);
  
  return {
    hours,
    region,
    regionalMultiplier,
    baseTokensPerHour,
    adjustedRate,
    estimatedTokens,
    estimatedValue: tokensToUSD(estimatedTokens),
    estimatedValueFormatted: formatTokenValue(estimatedTokens)
  };
};

/**
 * Get share/referral earning display info
 * @returns {Object} Share earning info for UI
 */
export const getShareEarnDisplay = () => {
  const sharerBonus = TOKEN_CONFIG.REFERRAL_BONUS_SHARER;
  const newUserBonus = TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER;
  const totalReferralValue = sharerBonus + newUserBonus;
  
  return {
    sharerBonus,
    sharerBonusValue: formatTokenValue(sharerBonus),
    newUserBonus,
    newUserBonusValue: formatTokenValue(newUserBonus),
    totalReferralValue,
    totalReferralValueFormatted: formatTokenValue(totalReferralValue),
    message: `Share UniLedge and earn ${sharerBonus} tokens (${formatTokenValue(sharerBonus)}) when friends sign up!`,
    friendMessage: `Your friend also gets ${newUserBonus} tokens (${formatTokenValue(newUserBonus)}) to start!`
  };
};

/**
 * Get recent job postings for the skills monetizer
 * @returns {Array} Mock job postings
 */
export const getRecentJobPostings = () => {
  return [
    {
      id: 'job-1',
      title: 'Local Delivery Driver Needed',
      type: 'delivery',
      tokens: 45,
      distance: '2.5 miles',
      estimatedTime: '30 min',
      postedAt: new Date(Date.now() - 1000 * 60 * 15) // 15 min ago
    },
    {
      id: 'job-2', 
      title: 'Group Order Coordinator',
      type: 'coordination',
      tokens: 30,
      members: 8,
      estimatedTime: '20 min',
      postedAt: new Date(Date.now() - 1000 * 60 * 45) // 45 min ago
    },
    {
      id: 'job-3',
      title: 'Quality Check Assistant',
      type: 'quality',
      tokens: 25,
      items: 12,
      estimatedTime: '15 min',
      postedAt: new Date(Date.now() - 1000 * 60 * 90) // 90 min ago
    }
  ];
};

export const calculateGroupTokenBudget = (group, adminTrustScore) => {
  const baseMonthlyBudget = TOKEN_POLICIES.GROUP_MONTHLY_TOKEN_BUDGET;
  const multiplier = Object.entries(TOKEN_POLICIES.TRUST_BUDGET_MULTIPLIERS)
    .reverse()
    .find(([score]) => adminTrustScore >= parseFloat(score))?.[1] || 0.5;
  const sizeFactor = Math.min((group.members?.length || 1) / 50, 2);
  const activityFactor = (group.completedOrders || 0) / 10;
  return Math.floor(baseMonthlyBudget * multiplier * sizeFactor * Math.min(activityFactor + 1, 1.5));
};

export const calculateMaxTokensForJob = (jobData) => {
  if (!jobData.requiredSkills || jobData.requiredSkills.length === 0) {
    return TOKEN_POLICIES.MAX_TOKENS_BY_TIER[1].base;
  }
  const maxTier = Math.max(...jobData.requiredSkills.map(s => s.tier || 1));
  const tierPolicy = TOKEN_POLICIES.MAX_TOKENS_BY_TIER[maxTier];
  const baseTokens = tierPolicy.base;
  const hourlyTokens = tierPolicy.perHour * (jobData.estimatedHours || 1);
  return Math.min(baseTokens + hourlyTokens, TOKEN_POLICIES.MAX_TOKENS_PER_JOB);
};

export const calculatePlatformTokens = (orderTotal) => {
  const platformFee = orderTotal * (TOKEN_POLICIES.PLATFORM_FEE_PERCENTAGE / 100);
  const tokenAllocation = platformFee * TOKEN_POLICIES.TOKEN_ALLOCATION_RATE;
  return {
    orderTotal,
    platformFee,
    tokenAllocation,
    tokensGenerated: Math.floor(tokenAllocation)
  };
};

// Export default config for easy access
export default TOKEN_CONFIG;